import React, { useEffect } from 'react';
import config from './config';
import { useAuth } from "react-oidc-context";
import { ThemeProvider, createTheme } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AppBar, Toolbar, Box, Button, Typography, IconButton, Drawer, List, ListItem, ListItemText, Divider } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from 'react-router-dom';

import PatientDatabase from './components/PatientDatabase';
import PatientScans from './pages/PatientScans';
import InvoicePage from './components/InvoicePage';
import ProductivityDashboard from './components/ProductivityDashboard';
import { PatientDataProvider } from './context/PatientDataContext';
import { logCognitoLogin, logLogout, setCognitoUser } from './api';
import './App.css';

// const API_BASE_URL = 'http://localhost:5000'; // Not used in this file

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function AppContent() {
  const { isLoading, isAuthenticated, error, signinRedirect, user, signoutRedirect } = useAuth();
  const [loginLogged, setLoginLogged] = React.useState(false);

  useEffect(() => {
    // Automatically redirect to sign in if not authenticated, not loading, and no error.
    if (!isLoading && !isAuthenticated && !error && !config.isDev) {
      signinRedirect();
    }
  }, [isLoading, isAuthenticated, error, signinRedirect]);

  // Log Cognito login event when user successfully authenticates (production)
  // Also log dev login on mount (development mode)
  useEffect(() => {
    if (config.isDev) {
      // Development mode: log dev login on mount
      const devUser = {
        profile: {
          email: config.devCredentials.username,
          sub: String(config.devCredentials.userId)
        },
        id_token: config.devCredentials.token
      };
      logCognitoLogin(devUser).catch(err => {
        console.error('Failed to log dev login:', err);
      });
    } else if (isAuthenticated && user && !loginLogged) {
      // Production mode: log Cognito login when authenticated
      // Store user globally so API calls can access Cognito token
      setCognitoUser(user);
      
      logCognitoLogin(user).then((result) => {
        if (result.success) {
          setLoginLogged(true);
        } else {
          // Retry once after a short delay if it failed
          setTimeout(() => {
            logCognitoLogin(user).then((retryResult) => {
              if (retryResult.success) {
                setLoginLogged(true);
              }
            });
          }, 1000);
        }
      });
    }
  }, [isAuthenticated, user, loginLogged]);
  
  // Update Cognito user when it changes
  useEffect(() => {
    if (isAuthenticated && user) {
      setCognitoUser(user);
    } else {
      setCognitoUser(null);
    }
  }, [isAuthenticated, user]);

  // In development, we can bypass the auth flow
  if (config.isDev) {
    const devUser = {
      profile: {
        email: config.devCredentials.username,
        sub: String(config.devCredentials.userId)
      },
      id_token: config.devCredentials.token,
      access_token: config.devCredentials.token // Use same token for dev
    };
    
    // Store dev user globally
    setCognitoUser(devUser);

    return <MainApp user={devUser} handleLogout={async () => {
      try {
        await logLogout();
      } catch (error) {
        console.error('Error logging dev logout:', error);
      }
      setCognitoUser(null); // Clear on logout
      console.log('Dev logout');
    }} />;
  }

  // Show a loading screen while the auth state is being determined or a redirect is in progress.
  if (isLoading || !isAuthenticated) {
    return <div>Loading...</div>;
  }

  // Show an error message if authentication fails.
  if (error) {
    return <div>Encountering error... {error.message}</div>;
  }

  // Create a logout handler that logs the event before redirecting
  const handleLogoutWithLogging = async () => {
    try {
      // Log logout event (fire and forget - don't wait for response)
      await logLogout();
    } catch (error) {
      console.error('Error logging logout:', error);
      // Continue with logout even if logging fails
    }
    setCognitoUser(null); // Clear Cognito user on logout
    // Call the original signoutRedirect
    signoutRedirect();
  };

  // If the user is authenticated, show the main application content.
  return <MainApp user={user} handleLogout={handleLogoutWithLogging} />;
}

function ProtectedRoute({ isAllowed, redirectPath, children }) {
  if (!isAllowed) {
    return <Navigate to={redirectPath} replace />;
  }
  return children;
}

function MainApp({ user, handleLogout }) {
  const userGroups = user?.profile?.['cognito:groups']?.map(g => g.toLowerCase()) || [];
  const isAdmin = userGroups.includes('admin') || String(user?.profile?.sub) === '1';
  const isClient = userGroups.includes('client');
  const isManager = userGroups.includes('manager');

  // Determine the default page based on role
  let defaultRedirect = '/database'; // Default for admins and managers
  if (isClient && !isAdmin && !isManager) {
    defaultRedirect = '/scans';
  }

  const [drawerOpen, setDrawerOpen] = React.useState(false);

return (
  <div className="App">
    <AppBar position="static">
      <Toolbar>
        {/* Hamburger for mobile */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, mr: 2 }}>
          <IconButton color="inherit" edge="start" onClick={() => setDrawerOpen(true)}>
            <MenuIcon />
          </IconButton>
        </Box>
        <Typography 
          variant="h6" 
          component="div" 
          sx={{ 
            fontWeight: 'bold', 
            mr: 3, 
            letterSpacing: 1,
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
          }}
        >
          <img src={`${process.env.PUBLIC_URL}/SandukePortal.png`} alt="Sanduke Portal Logo" style={{ height: '40px' }} />
        </Typography>
        {/* Desktop nav links */}
        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
          {(isAdmin || isManager) && (
            <NavLink 
              to="/database" 
              style={({ isActive }) => ({
                fontWeight: isActive ? 'bold' : 'normal',
                textDecoration: isActive ? 'underline' : 'none',
                color: 'inherit',
                marginRight: '16px',
                fontSize: '14px'
              })}
            >
              Dashboard
            </NavLink>
          )}
          {(isAdmin || isClient || isManager) && (
            <NavLink 
              to="/scans" 
              style={({ isActive }) => ({
                fontWeight: isActive ? 'bold' : 'normal',
                textDecoration: isActive ? 'underline' : 'none',
                color: 'inherit',
                marginLeft: '16px',
                marginRight: '16px',
                fontSize: '14px'
              })}
            >
              Patient Records
            </NavLink>
          )}
          {isAdmin && (
            <NavLink 
              to="/productivity" 
              style={({ isActive }) => ({
                fontWeight: isActive ? 'bold' : 'normal',
                textDecoration: isActive ? 'underline' : 'none',
                color: 'inherit',
                marginLeft: '16px',
                fontSize: '14px'
              })}
            >
              Productivity
            </NavLink>
          )}
        </Box>
        {/* Desktop user info */}
        <Box sx={{ ml: 'auto', display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
          <span style={{ marginRight: 16 }}>Logged in as <b>{user?.profile.email}</b></span>
          <Button color="inherit" onClick={handleLogout}>Logout</Button>
        </Box>
        {/* Drawer for mobile */}
        <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <Box sx={{ width: 250 }} role="presentation" onClick={() => setDrawerOpen(false)}>
            <List>
              {(isAdmin || isManager) && (
                <ListItem button component={NavLink} to="/database">
                  <ListItemText primary="Dashboard" />
                </ListItem>
              )}
              {(isAdmin || isClient || isManager) && (
                <ListItem button component={NavLink} to="/scans">
                  <ListItemText primary="Patient Records" />
                </ListItem>
              )}
              {isAdmin && (
                <ListItem button component={NavLink} to="/productivity">
                  <ListItemText primary="Productivity" />
                </ListItem>
              )}
            </List>
            <Divider />
            <List>
              <ListItem>
                <ListItemText primary={`Logged in as ${user?.profile.email || ''}`} />
              </ListItem>
              <ListItem button onClick={handleLogout}>
                <ListItemText primary="Logout" />
              </ListItem>
            </List>
          </Box>
        </Drawer>
      </Toolbar>
    </AppBar>

      <Routes>
        <Route 
          path="/database" 
          element={
            <ProtectedRoute isAllowed={isAdmin || isManager} redirectPath={defaultRedirect}>
              <PatientDatabase user={user} />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/scans" 
          element={
            <ProtectedRoute isAllowed={isAdmin || isClient || isManager} redirectPath={defaultRedirect}>
              <PatientScans userId={user?.profile.sub} user={user} />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/invoice" 
          element={
            <ProtectedRoute isAllowed={isAdmin || isManager} redirectPath={defaultRedirect}>
              <InvoicePage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/productivity" 
          element={
            <ProtectedRoute isAllowed={isAdmin} redirectPath={defaultRedirect}>
              <ProductivityDashboard />
            </ProtectedRoute>
          } 
        />
        <Route path="/" element={<Navigate to={defaultRedirect} replace />} />
        <Route path="*" element={<Navigate to={defaultRedirect} replace />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Router>
          <PatientDataProvider>
            <AppContent />
          </PatientDataProvider>
        </Router>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
